import { BenefitAdministrator, Track } from '@bariendo/lib/constants';
import { Autocomplete, Loader, Select } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { ReactElement, useRef, useState } from 'react';
import { checkEmployerBenefit, useEmployerSearch } from '@/hooks/useEmployerSearch';

export type EmployerPayor = Track.CARRUM | Track.TRANSCARENT;

const EMPLOYER_PAYOR_OPTIONS: { value: EmployerPayor; label: string }[] = [
  { value: Track.CARRUM, label: 'Carrum' },
  { value: Track.TRANSCARENT, label: 'Transcarent' },
];

const PAYOR_BY_ADMINISTRATOR: Record<BenefitAdministrator, EmployerPayor> = {
  [BenefitAdministrator.CARRUM]: Track.CARRUM,
  [BenefitAdministrator.TRANSCARENT]: Track.TRANSCARENT,
};

/**
 * The employer composer's first row (BAR-3077): Payor, and Employer as a typeahead over the
 * employer directory. Picking a directory employer sets Payor from its benefit check; a name
 * the directory lacks is kept as typed and leaves Payor alone.
 */
export function EmployerCoverageFields({
  payor,
  onPayorChange,
  employer,
  onEmployerChange,
  disabled,
}: {
  payor: EmployerPayor | null;
  onPayorChange: (payor: EmployerPayor | null) => void;
  employer: string;
  onEmployerChange: (employer: string) => void;
  disabled?: boolean;
}): ReactElement {
  const medplum = useMedplum();
  const [query, setQuery] = useState(employer);
  // The name last picked from the directory (or on file), which needs no search.
  const [picked, setPicked] = useState(employer);
  const [checking, setChecking] = useState(false);
  const { options, searching } = useEmployerSearch(query, { skip: disabled, current: picked });
  /** A check that returns after a newer pick changes nothing. */
  const latestPick = useRef(0);

  const pick = async (name: string): Promise<void> => {
    const pickId = ++latestPick.current;
    setPicked(name);
    setQuery(name);
    onEmployerChange(name);
    setChecking(true);
    try {
      const administrator = await checkEmployerBenefit(medplum, name);
      if (pickId === latestPick.current && administrator) onPayorChange(PAYOR_BY_ADMINISTRATOR[administrator]);
    } catch {
      // A failed check leaves Payor as the operator set it.
    } finally {
      if (pickId === latestPick.current) setChecking(false);
    }
  };

  return (
    <>
      <Select
        label="Payor"
        placeholder="Select a payor"
        data={EMPLOYER_PAYOR_OPTIONS}
        value={payor}
        onChange={(value) => onPayorChange(value as EmployerPayor | null)}
        disabled={disabled}
      />
      <Autocomplete
        label="Employer"
        placeholder="Search employers"
        data={options}
        value={query}
        onChange={(value) => {
          setQuery(value);
          onEmployerChange(value);
        }}
        onOptionSubmit={(value) => void pick(value)}
        rightSection={searching || checking ? <Loader size={12} /> : undefined}
        filter={({ options: all }) => all}
        disabled={disabled}
      />
    </>
  );
}
